<script setup lang="ts">
  import {
    BottomSheet,
    Button,
    ButtonAppearance,
    ButtonSize,
    Divider,
    DividerType,
    Picture,
    Toast,
    ToastSize,
    ToastType,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import type { ProductType } from '@/shared/config/pages';
  import { toCountryAccusative } from '@/shared/lib/toCountryAccusative';
  import type { Country } from '@/shared/model';
  import { PREPAYMENT_SHEET_TEXTS } from '../config/texts';
  import { formatPrepaymentDeadline } from '../lib/formatPrepaymentDeadline';
  import type { PrepaymentOperator, PrepaymentOption } from '../model/types';

  interface PrepaymentSheetProps {
    operators: PrepaymentOperator[];
    country?: Country | null;
    productType?: ProductType | null;
  }

  const props = defineProps<PrepaymentSheetProps>();

  interface PrepaymentSheetEmits {
    (e: 'close'): void;
  }

  const emit = defineEmits<PrepaymentSheetEmits>();

  // Страна знает только своё имя в именительном — склоняем её здесь: «По части туров в Турцию».
  const intro = computed<string>(() => {
    const country = props.country ? toCountryAccusative(props.country.id, props.country.name) : '';

    return [
      PREPAYMENT_SHEET_TEXTS.introPrefix,
      country && `${PREPAYMENT_SHEET_TEXTS.introCountryPrefix} ${country}`,
      PREPAYMENT_SHEET_TEXTS.introSuffix,
    ]
      .filter(Boolean)
      .join(' ');
  });

  const getPartLabel = (option: PrepaymentOption): string => `${PREPAYMENT_SHEET_TEXTS.optionPart} ${option.percent}%`;

  const getRestLabel = (option: PrepaymentOption): string =>
    option.deadline ? formatPrepaymentDeadline(option.deadline, props.productType ?? null) : '';

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  // Закрываем через сам BottomSheet, а не голым emit, чтобы шторка уехала с анимацией.
  const onClose = async (): Promise<void> => {
    await bottomSheetRef.value?.close();
  };
</script>

<template>
  <div
    class="prepayment-sheet"
    data-testid="prepayment-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="prepayment-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <template #header>
        <Typo
          class="prepayment-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="prepayment-sheet-title"
        >
          {{ PREPAYMENT_SHEET_TEXTS.title }}
        </Typo>
      </template>

      <div class="prepayment-sheet__content">
        <Typo
          class="prepayment-sheet__intro"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
          data-testid="prepayment-sheet-intro"
        >
          {{ intro }}
        </Typo>

        <Divider :type="DividerType.Dashed" />

        <!-- Справочник операторов приходит отдельной секцией поиска и может не приехать:
             без него шторка деградирует до вступления и предупреждения, без висящего пунктира. -->
        <template v-if="operators.length">
          <ul
            class="prepayment-sheet__operators"
            data-testid="prepayment-sheet-operators"
          >
            <li
              v-for="(operator, index) in operators"
              :key="operator.id"
              class="prepayment-sheet__operator-item"
            >
              <Divider
                v-if="index > 0"
                :type="DividerType.Dashed"
              />

              <div
                class="prepayment-sheet__operator"
                :data-testid="`prepayment-sheet-operator-${operator.id}`"
              >
                <Picture
                  v-if="operator.logo"
                  class="prepayment-sheet__operator-logo"
                  :path="operator.logo"
                  :extensions="[]"
                  :high-resolution="false"
                  fit="contain"
                  position="left center"
                />

                <div
                  v-for="option in operator.options"
                  :key="option.percent"
                  class="prepayment-sheet__option"
                >
                  <Typo
                    class="prepayment-sheet__option-part"
                    :variant="TypoVariant.BodyLMedium"
                    tag="span"
                  >
                    {{ getPartLabel(option) }}
                  </Typo>
                  <Typo
                    v-if="option.deadline"
                    class="prepayment-sheet__option-rest"
                    :variant="TypoVariant.BodyLRegular"
                    tag="span"
                  >
                    {{ getRestLabel(option) }}
                  </Typo>
                </div>
              </div>
            </li>
          </ul>

          <Divider :type="DividerType.Dashed" />
        </template>

        <!-- mobile — не про вьюпорт, а про ширину: без него ui-kit ставит тосту жёсткие 480px,
             и в шторке он вылезает за край, унося текст предупреждения в горизонтальный скролл. -->
        <Toast
          class="prepayment-sheet__note"
          :type="ToastType.ImportantInfo"
          :size="ToastSize.M"
          :mobile="true"
          :description="PREPAYMENT_SHEET_TEXTS.note"
          data-testid="prepayment-sheet-note"
        />
      </div>

      <template #footer>
        <Button
          class="prepayment-sheet__close"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.L"
          data-testid="prepayment-sheet-close"
          @click="onClose"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ PREPAYMENT_SHEET_TEXTS.close }}
          </Typo>
        </Button>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .prepayment-sheet {
    // Сам BottomSheet телепортируется в body, а на месте остаётся только скрытая обёртка. Без display: contents
    // она была бы flex-элементом карточки и на каждое открытие шторки добавляла ей лишний gap.
    display: contents;

    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
    }

    &__intro {
      color: var(--color-text-primary);
      margin: 0;
    }

    &__operators {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    // Разделитель живёт внутри элемента списка, чтобы отступ до него и после был одинаковым (20px).
    &__operator-item {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-l);
    }

    &__operator {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
    }

    // Логотипы у туроператоров разной пропорции: высота фиксирована, ширина ограничена сверху.
    &__operator-logo {
      height: 28px;
      max-width: 150px;
    }

    &__option {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxs);
    }

    &__option-part,
    &__option-rest {
      color: var(--color-text-primary);
    }

    &__close {
      width: 100%;
    }
  }
</style>
